import { LitElement, html, nothing, type CSSResultGroup, type TemplateResult } from 'lit';
import { property, state } from 'lit/decorators.js';
import styles from './styles.js';

/**
 * uwc-card
 *
 * A flexible content container with a structured header / body / footer layout.
 * A flexible content card component.
 *
 * @attr {string}  title      - Card title rendered in the body title block.
 * @attr {string}  subtitle   - Secondary line rendered below the title.
 * @attr {boolean} elevated   - Apply a drop shadow and remove the border.
 * @attr {boolean} flat       - Remove border and shadow for a ghost card.
 * @attr {boolean} horizontal - Lay the header (image) side-by-side with the body.
 *
 * @slot header   - Media zone above the body (typically an `<img>`). Fills edge-to-edge.
 * @slot title    - Overrides the `title` attribute with rich content.
 * @slot subtitle - Overrides the `subtitle` attribute with rich content.
 * @slot (default)- Main body content.
 * @slot footer   - Action row at the bottom, separated by a border.
 *
 * @cssprop --uwc-card-bg                       - Card background. Default: #fff.
 * @cssprop --uwc-card-border                   - Card border. Default: 1px solid rgba(0,0,0,.09).
 * @cssprop --uwc-card-radius                   - Border radius. Default: 14px.
 * @cssprop --uwc-card-shadow                   - Box shadow (elevated). Default: design token shadowMd.
 * @cssprop --uwc-card-color                    - Base text colour.
 * @cssprop --uwc-card-title-size               - Title font size. Default: 1.125rem.
 * @cssprop --uwc-card-title-color              - Title colour.
 * @cssprop --uwc-card-subtitle-size            - Subtitle font size. Default: 0.875rem.
 * @cssprop --uwc-card-subtitle-color           - Subtitle colour.
 * @cssprop --uwc-card-content-color            - Body content colour.
 * @cssprop --uwc-card-body-padding             - Body padding. Default: 1.25rem 1.5rem.
 * @cssprop --uwc-card-footer-padding           - Footer padding. Default: .875rem 1.5rem 1.25rem.
 * @cssprop --uwc-card-horizontal-header-width  - Header width in horizontal mode. Default: 220px.
 */
export class UwcCard extends LitElement {

  static override styles: CSSResultGroup = styles;

  @property({ reflect: true }) override     title    = '';
  @property({ reflect: true })             subtitle = '';
  @property({ type: Boolean, reflect: true }) elevated  = false;
  @property({ type: Boolean, reflect: true }) flat      = false;
  @property({ type: Boolean, reflect: true }) horizontal = false;

  @state() private _headerPopulated = false;
  @state() private _footerPopulated = false;

  // ── Slot detection ────────────────────────────────────────────────────────

  private _hasSlot(name: string): boolean {
    return this.querySelector(`[slot="${name}"]`) !== null;
  }

  private _onHeaderSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement;
    this._headerPopulated = slot.assignedNodes({ flatten: true }).length > 0;
  }

  private _onFooterSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement;
    this._footerPopulated = slot.assignedNodes({ flatten: true }).length > 0;
  }

  // ── Render ────────────────────────────────────────────────────────────────

  private _renderTitleBlock(): TemplateResult | typeof nothing {
    const hasTitle    = this.title    || this._hasSlot('title');
    const hasSubtitle = this.subtitle || this._hasSlot('subtitle');
    if (!hasTitle && !hasSubtitle) return nothing;

    return html`
      <div class="uwc-card__title-block">
        ${hasTitle ? html`
          <p class="uwc-card__title" part="title">
            ${this._hasSlot('title')
              ? html`<slot name="title"></slot>`
              : this.title}
          </p>
        ` : nothing}
        ${hasSubtitle ? html`
          <p class="uwc-card__subtitle" part="subtitle">
            ${this._hasSlot('subtitle')
              ? html`<slot name="subtitle"></slot>`
              : this.subtitle}
          </p>
        ` : nothing}
      </div>
    `;
  }

  override render() {
    return html`
      ${this._headerPopulated ? html`
        <div class="uwc-card__header" part="header">
          <slot name="header" @slotchange=${this._onHeaderSlotChange}></slot>
        </div>
      ` : html`<slot name="header" @slotchange=${this._onHeaderSlotChange}></slot>`}

      <div class="uwc-card__inner">
        <div class="uwc-card__body" part="body">
          ${this._renderTitleBlock()}
          <div class="uwc-card__content" part="content">
            <slot></slot>
          </div>
        </div>

        ${this._footerPopulated ? html`
          <div class="uwc-card__footer" part="footer">
            <slot name="footer" @slotchange=${this._onFooterSlotChange}></slot>
          </div>
        ` : html`<slot name="footer" @slotchange=${this._onFooterSlotChange}></slot>`}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-card': UwcCard; }
}
